import { CommonModule, DatePipe, NgComponentOutlet } from '@angular/common';
import { Component, computed, inject, input, Type } from '@angular/core';
import { contactLink } from '@web/shared/pipes/contact-link.pipe';
import { constructionSiteLink } from '@web/shared/pipes/construction-site-link.pipe';
import { employeeLink } from '@web/shared/pipes/employee-link.pipe';
import { projectLink } from '@web/shared/pipes/project-link.pipe';
import { realEstateLink } from '@web/shared/pipes/real-estate-link.pipe';
import { supplierLink } from '@web/shared/pipes/supplier-link.pipe';
import { bidLink } from '@web/shared/pipes/bid-link.pipe';
import { isEmpty } from 'class-validator';
import { TagModule } from 'primeng/tag';
import { BidTagComponent } from '../../bid-tag.component';
import { ConstructionSiteCodeTagComponent } from '../../construction-site-code-tag.component';
import { ContactCodeTagComponent } from '../../contact-code-tag.component';
import { EmployeeCodeTagComponent } from '../../employee-code-tag.component';
import { LeadCodeTagComponent } from '../../lead-code-tag.component';
import { OwnerCompanyCodeTagComponent } from '../../owner-company-code-tag.component';
import { ProjectCodeTagComponent } from '../../project-code-tag.component';
import { RealEstateCodeTagComponent } from '../../real-estate-code-tag.component';
import { ScoutingCodeTagComponent } from '../../scouting-code-tag.component';
import { SupplierCodeTagComponent } from '../../supplier-code-tag.component';
import { SearchNoteEntry, SearchNoteRelatedTo } from '../interfaces/search-notes-entry.interface';
import { DetailItem, SearchItemBaseComponent } from './search-item-base.component';

@Component({
  selector: 'app-search-note-item',
  imports: [CommonModule, TagModule, NgComponentOutlet, SearchItemBaseComponent],
  providers: [DatePipe],
  template: `
    @let _item = item();
    @let _relatedTo = _item.relatedTo;

    <app-search-item-base
      icon="pi pi-thumbtack"
      [detailRouterLink]="link()"
      [fragment]="fragment()"
      [color]="iconColor()"
      [detailItems]="detailItems()"
    >
      <ng-template #title>
        <div class="flex items-center gap-2 min-w-0">
          @if (_item.visibility === 'private') {
            <i class="pi pi-lock text-surface-500 text-sm! shrink-0" aria-hidden="true"></i>
          }
          <div class="min-w-0" [innerHTML]="_item.title"></div>
        </div>
      </ng-template>

      <ng-template #codeTag>
        @if (_relatedTo) {
          @let tagComponents = codeTagComponents[_relatedTo.aggregate];
          <div class="flex items-center gap-2">
            @if (_relatedTo.aggregate === 'Contact' && _relatedTo.name) {
              <span class="text-sm whitespace-nowrap">{{ _relatedTo.name }}</span>
            }

            <ng-container *ngComponentOutlet="tagComponents[0]; inputs: tagComponents[1](_item)" />
          </div>
        } @else {
          <span class="text-sm text-surface-500" i18n>Free note</span>
        }
      </ng-template>
    </app-search-item-base>
  `,
})
export class SearchNoteItemComponent {
  readonly item = input.required<SearchNoteEntry>();

  readonly datePipe = inject(DatePipe);

  readonly detailItems = computed<DetailItem[]>(() => {
    const item = this.item();

    return [
      {
        icon: 'pi pi-pen-to-square',
        content: item.body,
        isHtml: true,
      },
      {
        icon: 'pi pi-clock',
        content: this.datePipe.transform(item.createdAt) || '',
        isHtml: false,
      },
    ].filter((v) => !isEmpty(v.content));
  });

  readonly fragment = computed(() => (this.item().relatedTo ? undefined : this.item().id));

  readonly link = computed(() => {
    const item = this.item();
    const relatedTo = item.relatedTo;
    if (!relatedTo) {
      return ['/', 'annotations', 'inbox'];
    }

    const links: Record<SearchNoteRelatedTo['aggregate'], () => string[]> = {
      Contact: () =>
        contactLink({ id: relatedTo.id, kind: relatedTo.kind as 'naturalPerson' | 'legalPerson' }, ['notes']),
      RealEstate: () => realEstateLink({ id: relatedTo.id, kind: relatedTo.kind as 'unit' | 'group' }, ['notes']),
      Lead: () => ['/', 'leads', relatedTo.id, 'notes'],
      Scouting: () => ['/', 'scoutings', relatedTo.id, 'notes'],
      Employee: () => employeeLink({ id: relatedTo.id }, ['notes']),
      Supplier: () => supplierLink({ id: relatedTo.id, kind: relatedTo.kind as 'company' | 'freelance' }, ['notes']),
      OwnerCompany: () => ['/', 'owner-company'],
      Project: () => projectLink({ id: relatedTo.id }, ['notes']),
      ConstructionSite: () => constructionSiteLink({ id: relatedTo.id }, ['notes']),
      Bid: () => bidLink({ id: relatedTo.id }, ['notes']),
    };

    return links[relatedTo.aggregate]();
  });

  readonly iconColor = computed(() => {
    const relatedTo = this.item().relatedTo;
    if (!relatedTo) {
      return 'text-soe-dark-gray';
    }
    return this.iconColorByAggregate[relatedTo.aggregate];
  });

  readonly codeTagComponents: Record<
    SearchNoteRelatedTo['aggregate'],
    [Type<unknown>, (item: SearchNoteEntry) => Record<string, unknown>]
  > = {
    Contact: [ContactCodeTagComponent, (item: SearchNoteEntry) => ({ contact: item.relatedTo, clickable: false })],
    Lead: [LeadCodeTagComponent, (item: SearchNoteEntry) => ({ lead: item.relatedTo, clickable: false })],
    RealEstate: [
      RealEstateCodeTagComponent,
      (item: SearchNoteEntry) => ({ realEstate: item.relatedTo, clickable: false }),
    ],
    Scouting: [ScoutingCodeTagComponent, (item: SearchNoteEntry) => ({ scouting: item.relatedTo, clickable: false })],
    Employee: [EmployeeCodeTagComponent, (item: SearchNoteEntry) => ({ employee: item.relatedTo, clickable: false })],
    Supplier: [SupplierCodeTagComponent, (item: SearchNoteEntry) => ({ supplier: item.relatedTo, clickable: false })],
    OwnerCompany: [OwnerCompanyCodeTagComponent, () => ({ clickable: false })],
    Project: [ProjectCodeTagComponent, (item: SearchNoteEntry) => ({ project: item.relatedTo, clickable: false })],
    ConstructionSite: [
      ConstructionSiteCodeTagComponent,
      (item: SearchNoteEntry) => ({ constructionSite: item.relatedTo, clickable: false }),
    ],
    Bid: [BidTagComponent, (item: SearchNoteEntry) => ({ bid: item.relatedTo, clickable: false })],
  };

  readonly iconColorByAggregate: Record<SearchNoteRelatedTo['aggregate'], string> = {
    Contact: 'text-soe-twilight-blue',
    Lead: 'text-soe-tropical-green',
    RealEstate: 'text-soe-orange-glow',
    Scouting: 'text-soe-golden-sand',
    Employee: 'text-soe-orchid-purple',
    Supplier: 'text-soe-ocean-blue',
    OwnerCompany: 'text-soe-ocean-blue',
    Project: 'text-soe-sage-mist',
    ConstructionSite: 'text-soe-brick-clay',
    Bid: 'text-soe-petrol-blue',
  };
}
